// The story itself on a calm paper panel: every Prakrit word tappable (Word Explorer), a translation toggle per line
// and for all lines, the transliteration when it is switched on, and a play button only for recorded lines.
// Dialogues show the speaker of each line, with the two voices in two soft tones.

import type { FC, ReactNode } from 'react'
import type { ReaderLine, ReaderStory, Speaker } from '@/prakrit/components/stories/story-logic'

import { useState } from 'react'
import { Languages, MousePointerClick } from 'lucide-react'
import { cn } from '@/lib/utils'
import { audioKey, useAudio } from '@/prakrit/lib/client/audio'
import { transliterate } from '@/prakrit/lib/grammar/translit'
import { PrakritText, PrakritWord } from '@/prakrit/components/common/prakrit-word'
import { AudioButton } from '@/prakrit/components/common/audio-button'
import { hiDigits } from '@/prakrit/components/grammar/labs/lab-ui'

const speakerKey = (s: Speaker): string => `${s.pra ?? ''}|${s.hi}|${s.en}`

const TONE = [
    { tag: 'border-saffron/40 bg-saffron/10', row: '' },
    { tag: 'border-terracotta/40 bg-terracotta/8', row: 'sm:pl-12' }
]

const SpeakerTag: FC<{ speaker: Speaker, tone: number }> = ({ speaker, tone }): ReactNode => (
    <p className={cn('inline-flex w-fit flex-wrap items-baseline gap-x-1.5 rounded-full border px-3 py-0.5 text-sm', TONE[tone % TONE.length]?.tag)}>
        {speaker.pra ? (
            <span className='pra pra-sm'>
                <PrakritWord word={speaker.pra} />
            </span>
        ) : (
            <span lang='hi' className='hindi font-semibold text-foreground'>{speaker.hi}</span>
        )}
        {speaker.pra && <span lang='hi' data-ex='hi' className='hindi text-foreground/80'>{speaker.hi}</span>}
        <span aria-hidden='true' className='ex-sep text-muted-foreground/70'>·</span>
        <span lang='en' data-ex='en' className='text-muted-foreground'>{speaker.en}</span>
    </p>
)

interface LineRowProps {
    line: ReaderLine
    index: number
    storyId: string
    open: boolean
    onToggle: () => void
    tone: number | null
}

const LineRow: FC<LineRowProps> = ({ line, index, storyId, open, onToggle, tone }): ReactNode => {
    const panelId = `${storyId}-line-${index}-translation`

    return (
        <li className={cn('flex gap-3 rounded-xl px-2 py-3.5 transition-colors sm:gap-4 sm:px-3', open && 'bg-surface-muted/40', tone !== null && TONE[tone % TONE.length]?.row)}>
            <span aria-hidden='true' className='hindi w-7 shrink-0 pt-1.5 text-right text-sm font-semibold text-muted-foreground'>
                {hiDigits(index + 1)}
            </span>
            <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
                {line.speaker && tone !== null && <SpeakerTag speaker={line.speaker} tone={tone} />}
                <p className='pra pra-lg leading-[1.8]!'>
                    <PrakritText text={line.pra} />
                </p>
                <p lang='pra-Latn' className='translit text-sm italic text-muted-foreground'>{transliterate(line.pra)}</p>
                <div id={panelId} hidden={!open} className='rounded-lg border-l-4 border-saffron/50 bg-card px-3 py-2 text-[0.9375rem] leading-relaxed'>
                    <p lang='hi' data-ex='hi' className='hindi text-foreground'>{line.hi}</p>
                    <p lang='en' data-ex='en' className='text-muted-foreground'>{line.en}</p>
                </div>
                <div className='flex flex-wrap items-center gap-2 pt-0.5'>
                    <button
                        type='button'
                        aria-expanded={open}
                        aria-controls={panelId}
                        onClick={onToggle}
                        className={cn(
                            'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors in-data-[mode=kids]:h-10 in-data-[mode=kids]:text-sm',
                            open ? 'border-saffron/60 bg-saffron/10 text-foreground' : 'border-border bg-card hover:border-saffron/60 hover:bg-saffron/5'
                        )}
                    >
                        <Languages className='size-4' aria-hidden='true' />
                        <span lang='hi' className='hindi'>{open ? 'अनुवाद छिपाएँ' : 'अनुवाद'}</span>
                        <span lang='en' className='text-muted-foreground'>{open ? 'Hide' : 'Translation'}</span>
                        <span className='sr-only'>, line {index + 1}</span>
                    </button>
                    <AudioButton text={line.pra} size='sm' />
                </div>
            </div>
        </li>
    )
}

export const StoryText: FC<{ story: ReaderStory }> = ({ story }): ReactNode => {
    const [open, setOpen] = useState<boolean[]>(() => story.lines.map(() => false))
    const { ready, has } = useAudio()
    const allOpen = open.length > 0 && open.every(Boolean)
    const recorded = story.lines.some(line => has(audioKey(line.pra)))
    const dialogue = story.kind === 'dialogue'
    const speakers: string[] = []

    for (const line of story.lines) {
        if (line.speaker && !speakers.includes(speakerKey(line.speaker))) speakers.push(speakerKey(line.speaker))
    }

    const toggle = (index: number) => setOpen(previous => previous.map((value, i) => (i === index ? !value : value)))
    const toggleAll = () => setOpen(previous => previous.map(() => !allOpen))

    return (
        <section aria-labelledby='story-text-heading' className='overflow-hidden rounded-2xl border border-border bg-card shadow-card'>
            <div className='flex flex-wrap items-center justify-between gap-3 border-b border-border bg-surface-muted/50 px-5 py-3 sm:px-7'>
                <h2 id='story-text-heading' className='flex flex-wrap items-baseline gap-x-2'>
                    <span lang='hi' className='hindi text-lg font-bold'>{dialogue ? 'बातचीत पढ़ें' : 'कथा पढ़ें'}</span>
                    <span lang='en' className='font-sans text-sm font-medium text-muted-foreground'>{dialogue ? 'Read the dialogue' : 'Read the story'}</span>
                </h2>
                <button
                    type='button'
                    aria-pressed={allOpen}
                    onClick={toggleAll}
                    className='inline-flex min-h-tap items-center gap-2 rounded-xl border border-border bg-card px-4 text-sm font-medium transition-colors hover:border-saffron/60 hover:bg-saffron/5'
                >
                    <Languages className='size-4 text-saffron-strong' aria-hidden='true' />
                    <span lang='hi' className='hindi'>{allOpen ? 'सभी अनुवाद छिपाएँ' : 'सभी अनुवाद दिखाएँ'}</span>
                    <span lang='en' className='text-xs text-muted-foreground'>{allOpen ? 'Hide all translations' : 'Show all translations'}</span>
                </button>
            </div>

            <div className='px-4 pb-5 pt-4 sm:px-7 sm:pb-7'>
                <p className='flex items-start gap-2 px-2 text-sm text-muted-foreground sm:px-3'>
                    <MousePointerClick className='mt-0.5 size-4 shrink-0 text-saffron-strong' aria-hidden='true' />
                    <span>
                        <span lang='hi' data-ex='hi' className='hindi'>किसी भी शब्द पर टैप करें — उसका अर्थ और रूप खुलेगा।</span>
                        <span aria-hidden='true' className='ex-sep'> · </span>
                        <span lang='en' data-ex='en'>Tap any word to see its meaning and form.</span>
                    </span>
                </p>

                <div aria-hidden='true' className='leaf-rule mx-2 mb-2 mt-4 sm:mx-3' />

                <ol aria-label={dialogue ? 'बातचीत की पंक्तियाँ · Lines of the dialogue' : 'कथा की पंक्तियाँ · Lines of the story'} className='flex flex-col'>
                    {story.lines.map((line, i) => (
                        <LineRow
                            key={i}
                            line={line}
                            index={i}
                            storyId={story.id}
                            open={open[i] ?? false}
                            onToggle={() => toggle(i)}
                            tone={line.speaker ? speakers.indexOf(speakerKey(line.speaker)) : null}
                        />
                    ))}
                </ol>

                <div aria-hidden='true' className='leaf-rule mx-2 mt-2 sm:mx-3' />

                {ready && !recorded && (
                    <p className='mt-4 px-2 text-xs text-muted-foreground sm:px-3'>
                        <span lang='hi' data-ex='hi' className='hindi'>इस {dialogue ? 'बातचीत' : 'कथा'} को सुनने के लिए अभी कोई आवाज़ उपलब्ध नहीं।</span>
                        <span aria-hidden='true' className='ex-sep'> · </span>
                        <span lang='en' data-ex='en'>No audio for this {dialogue ? 'dialogue' : 'story'} on this device yet.</span>
                    </p>
                )}
            </div>
        </section>
    )
}
